import React from 'react';
import { DatePicker as AntDatePicker } from 'antd';
import customIcons from './custom-icons';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import classNames from 'classnames';
import { getString } from '@nackle/intl-tools';

dayjs.extend( customParseFormat );
dayjs.extend( advancedFormat );
dayjs.extend( weekday );
dayjs.extend( localeData );
dayjs.extend( weekOfYear );
dayjs.extend( weekYear );
require( 'dayjs/locale/de' );
require( 'dayjs/locale/fr' );
require( 'dayjs/locale/fr-ca' );
require( 'dayjs/locale/nl' );
require( 'dayjs/locale/pt-br' );
require( 'dayjs/locale/th' );
require( 'dayjs/locale/ru' );
require( 'dayjs/locale/zh-cn' );
require( 'dayjs/locale/zh-tw' );
require( 'dayjs/locale/ja' );
require( 'dayjs/locale/es' );
require( 'dayjs/locale/es-mx' );
require( 'dayjs/locale/en-gb' );
require( 'dayjs/locale/pl' );
require( 'dayjs/locale/it' );
require( 'dayjs/locale/ko' );
require( 'dayjs/locale/tr' );
dayjs.extend( localizedFormat );

const {
    calendarIcon,
    clearIcon,
    exchangeIcon,
    nextIcon,
    prevIcon,
    superNextIcon,
    superPrevIcon,
    error
} = customIcons;

/**
 * A Date Picker
 *
 * {@link https://ant.design/components/date-picker#common-api}
 *
 * @param {Object} props - The component props
 * @param {boolean} [props.fullWidth] - whether or not the date picker is full width
 * @param {string} [props.className] - the classname for the component
 * @param {string} [props.locale] - the locale of the date picker
 * @param {Array} [props.presetDays] - an array of preset days for the date picker
 * @returns {React.ReactElement} - a date picker component
**/
class DatePicker extends React.Component {

    render() {
        const { fullWidth, className, locale, presetDays } = this.props;
        let antDLocale;
        if ( typeof locale === 'string' ) {
            try {
                antDLocale = require( `antd/es/locale/${ locale === 'es-MX' ? 'es_ES' : locale.replace( '-', '_' ) }.js` ).default.DatePicker;
            } catch ( error ) {
                console.log( 'locale not supported, falling back to en-US', error );
                antDLocale = require( 'antd/es/locale/en_US.js' ).default.DatePicker;
            }
        } else if ( locale ) {
            antDLocale = locale;
        } else {
            antDLocale = require( 'antd/es/locale/en_US.js' ).default.DatePicker;
        }
        const pickerClasses = classNames( {
            'full-width': fullWidth ? true : false,
            [ className ]: className ? true : false
        } );
        const currentDate = dayjs();
        const cellRender = ( current, info ) => {
            const { type } = info;
            switch ( type ) {
                case 'date':
                    return info.originNode;
                case 'month':
                    if ( currentDate.month() === current.month() ) {
                        return <div className='current'>{ info.originNode }</div>;
                    } else {
                        return <div>{ info.originNode }</div>;
                    }
                case 'year':
                    if ( currentDate.year() === current.year() ) {
                        return <div className='current'>{ info.originNode }</div>;
                    } else {
                        return info.originNode;
                    }
                default:
                    return info.originNode;
            }
        };
        const presetArray = [];
        if ( presetDays ) {
            presetDays.map( days =>  {
                if ( Math.sign( days ) === -1 ) {
                    //days in the past
                    presetArray.push( { label: getString( 'origami.datepicker.preset', { presetDays: days } ), value: [ dayjs().add( parseInt( days ), 'd' ), dayjs() ] } );
                } else {
                    //days in the future
                    presetArray.push( { label: getString( 'origami.datepicker.presetFuture', { presetDays: days } ), value: [ dayjs().add( parseInt( days ), 'd' ), dayjs() ] } );
                }
            } );
        }
        return (
            <AntDatePicker
                //hacky workaround to get form validation status is to check the aria-invalid prop
                suffixIcon={ this.props.status === 'error' || this.props[ 'aria-invalid' ] === 'true' ? error : calendarIcon }
                allowClear={ { clearIcon: clearIcon } }
                nextIcon={ nextIcon }
                prevIcon={ prevIcon }
                superNextIcon={ superNextIcon }
                superPrevIcon={ superPrevIcon }
                cellRender={ cellRender }
                presets={ presetArray }
                { ...this.props }
                locale={ antDLocale }
                format={ antDLocale.lang.dateFormat }
                className={ pickerClasses }
            />
        );
    }
}

/**
 * A date range picker
 * @param {Object} props - the component props
 * @param {boolean} [props.fullWidth] - whether or not the range picker is full width
 * @param {string} [props.className] - the classname of the range picker
 * @param {string} [props.locale] - the locale of the range picker
 * @param {Array} [props.presetDays] - the pre selected dates in the range picker
 * @returns {React.ReactElement} A range picker component
**/
class RangePicker extends React.Component {
    render() {
        const { fullWidth, className, locale, presetDays } = this.props;
        let antDLocale;
        if ( typeof locale === 'string' ) {
            try {
                antDLocale = require( `antd/es/locale/${ locale === 'es-MX' ? 'es_ES' : locale.replace( '-', '_' ) }.js` ).default.DatePicker;
                //eslint-disable-next-line
            } catch ( err ) {
                console.log( 'locale not supported, falling back to en-US', error );
                antDLocale = require( 'antd/es/locale/en_US.js' ).default.DatePicker;
            }
        } else if ( locale ) {
            antDLocale = locale;
        } else {
            antDLocale = require( 'antd/es/locale/en_US.js' ).default.DatePicker;
        }
        const pickerClasses = classNames( {
            'full-width': fullWidth ? true : false,
            [ className ]: className ? true : false
        } );
        const currentDate = dayjs();
        const cellRender = ( current, info ) => {
            const { type } = info;
            switch ( type ) {
                case 'date':
                    return info.originNode;
                case 'month':
                    if ( currentDate.month() === current.month() & currentDate.year() === current.year() ) {
                        return <div className='current'>{ info.originNode }</div>;
                    } else {
                        return <div>{ info.originNode }</div>;
                    }
                case 'year':
                    if ( currentDate.year() === current.year() ) {
                        return <div className='current'>{ info.originNode }</div>;
                    } else {
                        return info.originNode;
                    }
                default:
                    return info.originNode;
            }
        };
        const presetArray = [];
        if ( presetDays ) {
            presetDays.map( days =>  {
                if ( Math.sign( days ) === -1 ) {
                    //days in the past
                    presetArray.push( { label: getString( 'origami.datepicker.preset', { presetDays: Math.abs( days ) } ), value: [ dayjs().add( parseInt( days ), 'd' ), dayjs() ] } );
                } else {
                    //days in the future
                    presetArray.push( { label: getString( 'origami.datepicker.presetFuture', { presetDays: days } ), value: [ dayjs().add( parseInt( days ), 'd' ), dayjs() ] } );
                }
            } );
        }
        return (
            <AntDatePicker.RangePicker
                //hacky workaround to get form validation status is to check the aria-invalid prop
                suffixIcon={ this.props.status === 'error' || this.props[ 'aria-invalid' ] === 'true' ? error : calendarIcon }
                allowClear={ { clearIcon: clearIcon } }
                separator={ exchangeIcon }
                nextIcon={ nextIcon }
                prevIcon={ prevIcon }
                superNextIcon={ superNextIcon }
                superPrevIcon={ superPrevIcon }
                cellRender={ cellRender }
                presets={ presetArray.length > 0 ? presetArray : undefined }
                { ...this.props }
                locale={ antDLocale }
                format={ antDLocale.lang.dateFormat }
                className={ pickerClasses }
            />
        );
    }
}

DatePicker.RangePicker = RangePicker;

export { DatePicker, dayjs };
